import React from 'react';
import './Manifesto.css';
import Header from './Header';
import Footer from './Footer';

const Manifesto = () => {
  return (
    <div className="manifesto-page">
      <Header />
      <section className="manifesto-section">
        <div className="container">
          <h1 className="section-title manifesto-title">Manifesto</h1>

          <div className="manifesto-content">
            <p className="manifesto-text">
              We do not sell software. We do not sell advice. We sell the thing that sits between them, the only thing that has ever actually closed the gap between owning AI and using it.
            </p>

            <h2 className="manifesto-heading">It Begins With a Week</h2>

            <p className="manifesto-text">
              It begins with a week, not a proposal. A forward-deployed engineer comes and sits inside the company. Not to interview executives about strategy, but to find out how the business actually runs. Which spreadsheet everyone secretly depends on. Which subscription nobody uses anymore but nobody's cancelled. Which piece of institutional knowledge lives in one person's head and nowhere else.
            </p>

            <p className="manifesto-text">
              By the end of the week, that picture exists as a live map, and so does something the company has never had before: a full, honest account of every tool it's paying for, and what each one is actually worth to it.
            </p>

            <h2 className="manifesto-heading">The Radar</h2>

            <p className="manifesto-text">
              From that map, a second thing is built automatically, and it never stops running. We call it the radar. It is a continuously updated backlog of every internal product worth building. Not a suggestion, a scored opportunity. Each one carries four numbers: how hard it is to build, what it costs, how far it can be shaped to the company's specific edge cases, and the number that changes everything: how much existing spend it replaces.
            </p>

            <p className="manifesto-text">
              The company doesn't receive a strategy deck. It receives a list it can act on, ranked in its own currency.
            </p>

            <h2 className="manifesto-heading">Then We Build</h2>

            <p className="manifesto-text">
              New capability first, always: the workflows that don't exist yet, the gaps nothing in the current stack fills. Nothing load-bearing is touched until trust has been earned the only way it can be: by shipping something small, watching it work, and doing it again.
            </p>

            <p className="manifesto-text">
              Only once that trust is real does the radar turn to displacement, replacing the tools the company is already paying for with something built for exactly how they work, not how a vendor imagined every company works.
            </p>

            <h2 className="manifesto-heading">Where It Stops Looking Like Consulting</h2>

            <p className="manifesto-text">
              Every tool we build for one company strengthens what we can build for the next. A connector written once gets reused, not rebuilt. A detection engine proven in one industry gets adapted, not reinvented. The tenth company we work with gets the speed and the price that only exist because nine came before it.
            </p>

            <p className="manifesto-text">
              This is not an efficiency we quietly keep. It's the reason the backlog gets cheaper and the build gets faster the longer we're in the room.
            </p>

            <h2 className="manifesto-heading">Who We Work With</h2>

            <p className="manifesto-text">
              We do not do this for everyone, and we are not built to. We hold a small number of companies at a time, the kind with real operations, a real budget, and no interest in hiring a platform team to chase a moving target alone.
            </p>

            <p className="manifesto-text">
              Established. Non-regulated. Not enterprise, not a startup. The businesses that make the world run and have simply never had a partner built for this problem before.
            </p>

            <h2 className="manifesto-heading">A Standing Capability</h2>

            <p className="manifesto-text">
              The relationship does not end when the first tools ship. It converts into a standing capability: a fixed amount of build capacity every month, priced in the plainest terms we can offer, that turns into new tools, new integrations, new products as the frontier moves and the radar fills with things that didn't exist to build six months ago.
            </p>

            <p className="manifesto-text">
              When we replace something the company was already paying for, we take a share of exactly what we saved them. Nothing more, nothing hidden.
            </p>

            <h2 className="manifesto-heading">This Is the Product</h2>

            <p className="manifesto-text">
              A map of how the business really works, a radar that never stops finding what to build next, a team that shows up for a week and stays for years, and a library that makes every company we work with faster to serve than the one before it.
            </p>

            <p className="manifesto-text manifesto-final">
              Everyone is selling AI. We are the ones who make it real.
            </p>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default Manifesto;
